import { useMemo } from "react"

// ** Types
import { AnalyticsFilters } from "@pma/types";

// ** Helpers
import { getChartColor } from "@pma/utils/helpers";

export function useDiaryComputed({
  allDiaryData,
  filters,
}: {
  allDiaryData: { project: any; diaries: any[] }[] | undefined
  filters?: AnalyticsFilters
}) {
  return useMemo(() => {
    let total = 0
    let recent = 0
    const byProject: { projectId: number; name: string; count: number; fill: string }[] = []
    const approvalMap: Record<string, number> = { approved: 0, pending: 0, rejected: 0 }
    const now = new Date()
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
    const allFilteredDiariesWithMeta: {
      diary: any
      projectId: number
      projectName: string
    }[] = []

    ;(allDiaryData || []).forEach(({ project, diaries }, idx) => {
      let filteredDiaries = diaries

      if (filters?.dateRange?.from) {
        const from = filters.dateRange.from
        filteredDiaries = filteredDiaries.filter(
          (d: any) => new Date(d?.date || d?.created_at || 0) >= from,
        )
      }
      if (filters?.dateRange?.to) {
        const to = filters.dateRange.to
        filteredDiaries = filteredDiaries.filter(
          (d: any) => new Date(d?.date || d?.created_at || 0) <= to,
        )
      }

      if (filters?.userIds?.length) {
        filteredDiaries = filteredDiaries.filter((d: any) => {
          const createdById =
            d?.created_by?.id || d?.created_by_id || d?._created_by?.id
          return createdById && filters.userIds.includes(createdById)
        })
      }

      const count = filteredDiaries.length
      total += count

      byProject.push({
        projectId: project.id,
        name: project.title || project.name || `Project ${idx + 1}`,
        count,
        fill: getChartColor(idx),
      })

      filteredDiaries.forEach((diary: any) => {
        const diaryDate = new Date(diary?.date || diary?.created_at || 0)
        if (diaryDate >= weekAgo) recent++

        const sa = diary?.supervisor_approval
        const ca = diary?.contractor_approval
        if (sa === true && ca === true) {
          approvalMap["approved"]++
        } else if (sa === false || ca === false) {
          approvalMap["rejected"]++
        } else {
          approvalMap["pending"]++
        }

        allFilteredDiariesWithMeta.push({
          diary,
          projectId: project.id,
          projectName: project.title || project.name || `Project ${idx + 1}`,
        })
      })
    })

    byProject.sort((a, b) => b.count - a.count)

    const sortedDiaries = [...allFilteredDiariesWithMeta].sort((a, b) => {
      const da = new Date(a.diary?.date || a.diary?.created_at || 0).getTime()
      const db = new Date(b.diary?.date || b.diary?.created_at || 0).getTime()
      return db - da
    })

    return {
      totalDiaries: total,
      diariesByProject: byProject.filter((p) => p.count > 0),
      approvalBreakdown: approvalMap,
      recentDiariesCount: recent,
      recentDiaries: sortedDiaries.slice(0, 5),
      allFilteredDiaries: allFilteredDiariesWithMeta,
    }
  }, [allDiaryData, filters?.dateRange, filters?.userIds])
}
